import { lazy, Suspense, useEffect, useRef, useState, type FocusEvent, type MouseEvent as ReactMouseEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { IconCollectibles, IconHooks, IconJournal, IconOperator, IconRewards, IconRun, IconToken, IconVerify, Wordmark as BrandWordmark } from '@runner/ui';
import { Link, useRoute } from './router';
import { useWorld, useWorldCtx } from '../data/WorldProvider';
import { WalletModal } from '../components/WalletModal';
import { CHAIN_ID, DEMO_ENABLED } from '../data/api/config';
import { networkInfo } from '../data/chain/chains';
import { ConnectionBanner, FreshnessBadge, ModeBadge, PhasePill } from '../components/game';
import { RunPage } from '../pages/RunPage';
import { useEventTheme } from '../components/RunDay';

const AccountPage = lazy(() => import('../pages/AccountPage').then((m) => ({ default: m.AccountPage })));
const TradePage = lazy(() => import('../pages/TradePage').then((m) => ({ default: m.TradePage })));
const RewardsPage = lazy(() => import('../pages/RewardsPage').then((m) => ({ default: m.RewardsPage })));
const HooksPage = lazy(() => import('../pages/HooksPage').then((m) => ({ default: m.HooksPage })));
const JournalPage = lazy(() => import('../pages/JournalPage').then((m) => ({ default: m.JournalPage })));
const TokenPage = lazy(() => import('../pages/TokenPage').then((m) => ({ default: m.TokenPage })));
const VerifyPage = lazy(() => import('../pages/VerifyPage').then((m) => ({ default: m.VerifyPage })));
const OperatorPage = lazy(() => import('../pages/OperatorPage').then((m) => ({ default: m.OperatorPage })));
const RunnersPage = lazy(() => import('../pages/RunnersPage').then((m) => ({ default: m.RunnersPage })));

const NAV: [string, string, (p: { size?: number }) => ReactNode][] = [
  ['/', 'Race', IconRun],
  ['/account', 'Your account', IconCollectibles],
  ['/rewards', 'Rewards', IconRewards],
  ['/runners', 'My Runners', IconCollectibles],
  ['/hooks', 'Hooks', IconHooks],
  ['/journal', 'Journal', IconJournal],
  ['/token', 'Token & Treasury', IconToken],
  ['/verify', 'Verify', IconVerify],
  ['/operator', 'Operator', IconOperator],
];
const PRIMARY_MOBILE = ['/', '/account'];
/** Account is primary; the remaining pages live in More. */
const PRIMARY_DESKTOP = ['/', '/account'];

/** Play lives at / and also answers /run; the full trade form (/trade) is part of it, so Play stays highlighted on all three. */
const playing = (to: string, path: string) =>
  to === '/' && ['/run', '/trade'].some((p) => path === p || path.startsWith(p + '/')) ? { 'aria-current': 'page' as const } : {};

function Wordmark() {
  return (
    <Link to="/" className="wordmark" aria-label="RUNNER home">
      {/* Small companion wordmark: the brand's choice for navigation widths (96–159 px). */}
      <BrandWordmark small />
    </Link>
  );
}

function ModeSwitch() {
  const { mode, setMode, localAvailable } = useWorldCtx();
  if (!DEMO_ENABLED) return null;
  return (
    <div className="segmented" role="group" aria-label="Data source">
      <button aria-pressed={mode === 'demo'} onClick={() => setMode('demo')}>
        Demo
      </button>
      <button
        aria-pressed={mode === 'local'}
        onClick={() => setMode('local')}
        title={
          CHAIN_ID !== 31337
            ? `Live data from ${networkInfo(CHAIN_ID).name} through the RUNNER API`
            : localAvailable === false
              ? 'Local chain API not reachable (run pnpm local:up, contracts:deploy:local, worker:dev, api and dev)'
              : 'Use the local Anvil chain through the RUNNER API'
        }
      >
        {CHAIN_ID === 31337 ? 'Local chain' : networkInfo(CHAIN_ID).kind === 'mainnet' ? 'Live' : networkInfo(CHAIN_ID).name}
      </button>
    </div>
  );
}

function closeMenuOnEscape(e: ReactKeyboardEvent<HTMLDetailsElement>) {
  if (e.key === 'Escape') { e.currentTarget.open = false; e.currentTarget.querySelector('summary')?.focus(); }
}
function closeMenuOnBlur(e: FocusEvent<HTMLDetailsElement>) {
  if (!e.currentTarget.contains(e.relatedTarget)) e.currentTarget.open = false;
}

function closeMenuOnNavigate(e: ReactMouseEvent<HTMLDivElement>) {
  if ((e.target as HTMLElement).closest('a')) {
    const menu = e.currentTarget.closest('details');
    if (menu) menu.open = false;
  }
}

function WalletButton() {
  const { view, source } = useWorld();
  const { mode } = useWorldCtx();
  const [picking, setPicking] = useState(false);
  const u = view.user;
  if (mode === 'demo') return <span className="badge wallet-badge" data-testid="wallet-status" data-connected={u.connected ? 'yes' : 'no'}>Demo wallet</span>;
  if (u.connected) return (
    <details className="header-wallet" data-testid="wallet-status" data-connected="yes" onKeyDown={closeMenuOnEscape} onBlur={closeMenuOnBlur}>
      <summary aria-label="Wallet options"><span>Wallet</span><span className="wallet-address">{u.walletKind === 'dev' ? 'Dev key #5' : u.address ? `${u.address.slice(0, 6)}…${u.address.slice(-4)}` : u.label}</span><span aria-hidden="true">⌄</span></summary>
      <div className="header-wallet-panel" onClick={closeMenuOnNavigate}>
        <span className="small muted">{view.network?.name ?? view.networkLabel}</span>
        <span className="mono small wallet-full-address">{u.address}</span>
        <Link to="/account">Your account & rewards ↗</Link>
        <button className="btn small ghost" onClick={() => source.disconnect()}>Disconnect</button>
      </div>
    </details>
  );
  // Several browser wallets (Phantom, MetaMask, Rabby…) can be installed at once: the dialog lists them.
  const connect = (walletId?: string) => {
    setPicking(false);
    void source.connect('injected', walletId).catch(() => {/* shown inline via walletError */});
  };
  const start = () => setPicking(true);
  return (
    <span className="wallet-actions">
      <button className="btn small primary" data-testid="connect-wallet" disabled={u.connecting} onClick={start}>
        {u.connecting ? 'Connecting…' : 'Connect wallet'}
      </button>
      {view.network?.kind === 'local' && (
        <button className="btn small" title="Anvil public development key #5 — disposable, local chain only" onClick={() => void source.connect('dev').catch(() => {})}>
          Dev key
        </button>
      )}
      {picking && <WalletModal networkName={view.network?.name ?? 'Robinhood Chain'} onPick={connect} onClose={() => setPicking(false)} />}
    </span>
  );
}

/** Inline wallet problems: no wallet, rejection, wrong network. Never a blocking alert dialog. */
function WalletNotice() {
  const { view, source } = useWorld();
  const u = view.user;
  if (view.mode === 'demo') return null;
  if (u.wrongChain) {
    return (
      <div className="notice warn" role="alert" data-testid="wrong-network">
        <strong>Wrong network.</strong> Your wallet is on chain {u.walletChainId}; this RUNNER deployment is on{' '}
        {view.network ? `${view.network.name} (chain ${view.network.id})` : 'another chain'}. Transactions are blocked until
        you switch.{' '}
        <button className="btn small" onClick={() => void source.switchChain?.()}>
          Switch network
        </button>
        {u.walletError && <div className="small">{u.walletError}</div>}
      </div>
    );
  }
  if (!u.walletError) return null;
  return (
    <div className="notice warn" role="alert" data-testid="wallet-error">
      {u.walletError}{' '}
      <button className="btn small ghost" onClick={() => source.dismissWalletError?.()}>
        Dismiss
      </button>
    </div>
  );
}

/** Mobile "More" sheet: every page stays reachable without the desktop sidebar. */
function MoreSheet({ onClose }: { onClose(): void }) {
  const { view } = useWorld();
  const ref = useRef<HTMLDivElement>(null);
  // Latest onClose via a ref: focus moves only on open and close, not on every live update (see TradeDrawer).
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const on = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    window.addEventListener('keydown', on);
    return () => {
      window.removeEventListener('keydown', on);
      prev?.focus();
    };
  }, []);
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label="More pages and settings" tabIndex={-1} ref={ref}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>More</h2>
          <button className="btn small ghost" onClick={onClose}>
            Close
          </button>
        </div>
        <nav aria-label="More pages" className="sheet-links" onClick={onClose}>
          {NAV.filter(([to]) => !PRIMARY_MOBILE.includes(to)).map(([to, label, Icon]) => (
            <Link key={to} to={to} className="nav-link">
              <Icon /> {label}
            </Link>
          ))}
        </nav>
        <div className="stack" style={{ gap: 8 }}>
          {DEMO_ENABLED && <span className="small muted">Data source</span>}
          <ModeSwitch />
          <span className="small muted">{view.networkLabel}</span><FreshnessBadge view={view} />
        </div>
      </div>
    </>
  );
}

function Page() {
  const { path } = useRoute();
  if (path === '/' || path === '' || path === '/run' || path.startsWith('/run/')) return <RunPage />;
  const map: Record<string, ReactNode> = {
    '/account': <AccountPage />,
    '/trade': <TradePage />,
    '/rewards': <RewardsPage />,
    '/runners': <RunnersPage />,
    '/hooks': <HooksPage />,
    '/journal': <JournalPage />,
    '/token': <TokenPage />,
    '/verify': <VerifyPage />,
    '/operator': <OperatorPage />,
  };
  const key = Object.keys(map).find((k) => path === k || path.startsWith(k + '/'));
  return key ? (
    <Suspense fallback={<div className="page">Loading…</div>}>{map[key]}</Suspense>
  ) : (
    <div className="page">
      <h1 className="page-title">Off course</h1>
      <p className="lede">That page does not exist.</p>
      <Link to="/run" className="btn primary">
        Back to the run
      </Link>
    </div>
  );
}

function HeaderNavigation() {
  const { view } = useWorld();
  const { path } = useRoute();
  const moreActive = !['/', '/run', '/trade', '/account'].some(p => path === p || (p !== '/' && path.startsWith(p + '/')));
  return <nav className="play-nav" aria-label="Main">
    <Link to="/account" className="header-account">Your account</Link>
    <details className="protocol-nav" onKeyDown={closeMenuOnEscape} onBlur={closeMenuOnBlur}>
      <summary aria-current={moreActive ? 'page' : undefined}>More <span aria-hidden="true">⌄</span></summary>
      <div className="protocol-links" onClick={closeMenuOnNavigate}>
        {NAV.filter(([to]) => !PRIMARY_DESKTOP.includes(to)).map(([to, label, Icon]) => <Link key={to} to={to} className="nav-link secondary"><Icon size={18} />{label}</Link>)}
        <div className="nav-foot"><ModeSwitch /><span>{view.networkLabel}</span><FreshnessBadge view={view} /><PhasePill view={view} /></div>
      </div>
    </details>
  </nav>;
}

export function App() {
  const { view } = useWorld();
  const { path } = useRoute();
  const [more, setMore] = useState(false);
  const chrome = useRef<HTMLDivElement>(null);
  useEventTheme();
  useEffect(() => {
    const el = chrome.current;
    const shell = el?.closest<HTMLElement>('.shell');
    if (!el || !shell) return;
    const measure = () => shell.style.setProperty('--app-header-h', `${el.offsetHeight}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const liveMainnet = view.mode === 'local' && view.network?.kind === 'mainnet' && view.freshness.api !== 'not-used';
  const moreActive = !path.startsWith('/trade') && !path.startsWith('/run') && !PRIMARY_MOBILE.some((p) => (p === '/' ? path === '/' : path === p || path.startsWith(p + '/')));
  return (
    <div className="shell shell-game">
      <a href="#main" className="skip">
        Skip to content
      </a>
      <div className="main">
        <div className="app-chrome" ref={chrome}>
          <header className="topbar">
            <div className="header-brand"><Wordmark />{!liveMainnet && <span className="header-mode"><ModeBadge view={view} /></span>}</div>
            <HeaderNavigation />
            <div className="right">
              <WalletButton />
            </div>
          </header>
          <WalletNotice />
          <ConnectionBanner view={view} />
        </div>
        <main id="main" tabIndex={-1}>
          <Page />
        </main>
      </div>
      <nav className="bottom-nav" aria-label="Main (mobile)">
        {NAV.filter(([to]) => PRIMARY_MOBILE.includes(to)).map(([to, label, Icon]) => (
          <Link key={to} to={to} {...playing(to, path)}>
            <Icon size={20} />
            {label.replace('My ', '')}
          </Link>
        ))}
        <button type="button" className="more-btn" aria-haspopup="dialog" aria-expanded={more} aria-current={moreActive ? 'page' : undefined} onClick={() => setMore(true)}>
          <span aria-hidden="true" className="more-dots">
            •••
          </span>
          More
        </button>
      </nav>
      {more && <MoreSheet onClose={() => setMore(false)} />}
    </div>
  );
}
